import { Observable } from "rxjs";
import { List } from "immutable";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { IOperation } from "actions/mutate";
import { getAndIncrementRequestId } from "./helpers";
import { getReviewPlanFromState } from "reducers/selectors/collection";
import { REVIEW_PLAN_ID_PREFIX } from "constants/product-collection";
import { PlanMetricRows } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { getLegacyTypeByMetricsKind } from "helpers/metrics/legacy-metrics";
interface IReviewPlanRequest {
    set_value: {
        dimension_value_ids: string[];
        plan_detail: {
            depth: string;
            retail: string;
            skus: number;
            units: number;
            wholesale: string;
        };
    };
}
export const DIMENSION_IDS_DELIMITER = "#";

const normalizeReviewPlanOperation = (operation: IOperation): IReviewPlanRequest => {
    const { value, key } = operation;
    const planDetail = value.get("struct_value").toJS();
    return {
        set_value: {
            dimension_value_ids: key.split(DIMENSION_IDS_DELIMITER),
            plan_detail: planDetail,
        },
    };
};

const getDiffIndexesForRows = (operation: IOperation, planMetrics: string[]): { [key: string]: boolean } => {
    const diffIndexes = {};
    if (operation.deltas || operation.value) {
        Object.keys(PlanMetricRows).forEach((planIndex) => {
            diffIndexes[planIndex] = true;
        });
    }
    return diffIndexes;
};
export const syncReviewPlanEpic = (actions$, store) =>
    actions$
        .ofType(COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();
            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId } }: { payload: { objectId: string } }) => {
            const { collection, mutate, dimensionsPanel } = store.getState();
            const planMetrics = dimensionsPanel.applied.metrics.values.map((metric) =>
                getLegacyTypeByMetricsKind(metric.kind),
            );
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            let diffIndexes = {};
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    diffIndexes = {
                        ...diffIndexes,
                        ...getDiffIndexesForRows(operation, planMetrics),
                    };
                    operations.push(normalizeReviewPlanOperation(operation));
                });
            });

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                operations,
                revision,
                req_id: requestId,
                allow_transformation: true,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            const reviewPlanId = objectId.replace(REVIEW_PLAN_ID_PREFIX, "");

            return {
                command,
                sessionId,
                requestId,
                reviewPlanId,
                collectionId: committedHEAD.document_id,
                diffIndexes,
                merchantId,
            };
        })
        .concatMap(({ command, collectionId, requestId, sessionId, reviewPlanId, diffIndexes, merchantId }) => {
            const url = `/v1/collections/${collectionId}/review_plans/${reviewPlanId}/mutate?sid=${sessionId}&req_id=${requestId}`;
            return ajax({
                body: { ...command, merchant_id: merchantId },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId, diffIndexes },
                    type: COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED,
                }))
                .catch((error) => {
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED,
                        }),
                    );
                });
        });

export const updateReviewPlanRowsEpic = (actions$, store) =>
    actions$.ofType(COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED).concatMap(({ payload }) => {
        const { collectionId, diffIndexes } = payload;
        const state = store.getState();
        const {
            routing: { locationBeforeTransitions },
        } = state;
        const { poi } = locationBeforeTransitions.query;
        const { reviewPlan } = getReviewPlanFromState(state, collectionId, poi);
        const mutations = [];

        if (diffIndexes) {
            mutations.push({
                type: COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.UPDATE,
                payload: {
                    indexes: Object.keys(diffIndexes),
                    reviewPlan: reviewPlan.toJS(),
                },
            });
        }

        if (reviewPlan) {
            mutations.push({
                type: COLLECTIONS.COLLECTION.REVIEW_PLANS.LOAD,
                payload: {
                    reviewPlan: reviewPlan,
                },
            });
        }

        return mutations;
    });
